import NotificationIcon from '@atlaskit/icon/core/notification';
import ToolsIcon from '@atlaskit/icon/core/tools';
import SyncIcon from '@atlaskit/icon-lab/core/sync';
import React from 'react';
import { useIntl } from 'react-intl';

import { AnalyticsSideNavItem } from '@townsquare/analytics-nav4';
import { filterUndefined } from '@townsquare/filter-type';
import { SideNavItem } from '@townsquare/griffin-navigation/types';
import { SideNavProductItem } from '@townsquare/navigation-config/nav4';
import { useOrgContextIsOrgAdmin } from '@townsquare/org-context';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { generatePath } from '@townsquare/router/primitives/path';
import { teamsSettingsRoute } from '@townsquare/routes/teams-settings-view';
import { fg } from '@townsquare/stat-sig/gate';

import { SettingsMenuItem, type SettingsMenuItemProps } from './SettingsMenuItem';

export const useSideNavSettingsItems = (): SideNavItem[] => {
	const intl = useIntl();
	const routeDependencies = useRouteDependencies();
	const isOrgAdmin = useOrgContextIsOrgAdmin();

	const notificationsItem = {
		id: SideNavProductItem.TEAMS_SETTINGS_NOTIFICATIONS,
		analyticsSubject: AnalyticsSideNavItem.TEAMS_SETTINGS_NOTIFICATIONS,
		path: `${generatePath(teamsSettingsRoute, routeDependencies)}?&screen=NOTIFICATIONS`,
		matches: [],
		Component: (props: SettingsMenuItemProps) => (
			<SettingsMenuItem {...props} matchingDirectoryScreens={['NOTIFICATIONS']} />
		),
		name: intl.formatMessage({
			id: 'townsquare.teams-settings.notifications.name',
			defaultMessage: 'Notifications settings',
			description: 'Name for the notifications settings page in Teams Settings',
		}),
		Icon: NotificationIcon,
		testId: 'side-nav-teams-settings-notifications',
	};

	if (!isOrgAdmin) {
		return [notificationsItem];
	}

	return [
		fg('enable_atlassian_team_types_m1')
			? {
					id: SideNavProductItem.TEAMS_SETTINGS_TEAM_TYPES,
					analyticsSubject: AnalyticsSideNavItem.TEAMS_SETTINGS_TEAM_TYPES,
					path: `${generatePath(teamsSettingsRoute, routeDependencies)}?&screen=TEAM_TYPES`,
					matches: [teamsSettingsRoute],
					Component: (props: SettingsMenuItemProps) => (
						<SettingsMenuItem
							{...props}
							matchingDirectoryScreens={['TEAM_TYPES', 'TEAM_TYPES_CREATE', 'TEAM_TYPES_EDIT']}
						/>
					),
					name: intl.formatMessage({
						id: 'townsquare.teams-settings.team-types.name',
						defaultMessage: 'Team types',
						description: 'Name for the Team Types settings page in Teams Settings',
					}),
					Icon: ToolsIcon,
					testId: 'side-nav-teams-settings-team-types',
				}
			: undefined,
		{
			id: SideNavProductItem.TEAMS_SETTINGS_HRIS_CONNECTORS,
			analyticsSubject: AnalyticsSideNavItem.TEAMS_SETTINGS_HRIS_CONNECTORS,
			path: `${generatePath(teamsSettingsRoute, routeDependencies)}?&screen=HRIS_CONNECTORS`,
			matches: fg('enable_atlassian_team_types_m1') ? [] : [teamsSettingsRoute],
			Component: (props: SettingsMenuItemProps) => (
				<SettingsMenuItem {...props} matchingDirectoryScreens={['HRIS_CONNECTORS']} />
			),
			name: intl.formatMessage({
				id: 'townsquare.teams-settings.hris-connections.name',
				defaultMessage: 'HRIS connections',
				description: 'Name for the HRIS Connectors settings page in Teams Settings',
			}),
			Icon: SyncIcon,
			testId: 'side-nav-teams-settings-hris-connectors',
		},
		fg('enable_atlassian_team_custom_fields_m1')
			? {
					id: SideNavProductItem.TEAMS_SETTINGS_CUSTOM_FIELDS,
					analyticsSubject: AnalyticsSideNavItem.TEAMS_SETTINGS_CUSTOM_FIELDS,
					path: `${generatePath(teamsSettingsRoute, routeDependencies)}?&screen=CUSTOM_FIELDS`,
					matches: [],
					Component: (props: SettingsMenuItemProps) => (
						<SettingsMenuItem {...props} matchingDirectoryScreens={['CUSTOM_FIELDS']} />
					),
					name: intl.formatMessage({
						id: 'townsquare.teams-settings.custom-fields.name',
						defaultMessage: 'Fields',
						description: 'Name for the custom fields settings page in Teams Settings',
					}),
					Icon: ToolsIcon,
					testId: 'side-nav-teams-settings-custom-fields',
				}
			: undefined,
		notificationsItem,
	].filter(filterUndefined);
};
